(() => {
  const SELECTORS = {
    root: '[data-sy-topic-library]',
    grid: '[data-topic-grid]',
    card: '[data-topic-resource]',
    next: '[data-topic-next]',
    pagination: '[data-topic-pagination]',
    search: '[data-topic-search]',
    formatRadio: '[data-topic-format]',
    formatSelect: '[data-topic-format-select]',
    levelRadio: '[data-topic-level]',
    levelSelect: '[data-topic-level-select]',
    sort: '[data-topic-sort]',
    clear: '[data-topic-clear]',
    empty: '[data-topic-empty]',
    emptyReset: '[data-topic-empty-reset]',
    count: '[data-topic-result-count]',
    status: '[data-topic-status]',
    heroResourceCount: '[data-topic-hero-resource-count]',
    heroFormatCount: '[data-topic-hero-format-count]',
    heroLevelFact: '[data-topic-hero-level-fact]',
    heroLevelCount: '[data-topic-hero-level-count]',
    heroLevelNames: '[data-topic-hero-level-names]'
  };

  const DEFAULT_STATE = {
    query: '',
    format: 'all',
    level: 'all',
    sort: 'recommended'
  };

  const parseNumber = (value) => Number.parseInt(value || '0', 10) || 0;

  class TopicLibrary {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector(SELECTORS.grid);
      this.sectionId = root.dataset.sectionId;
      this.cards = [];
      this.state = { ...DEFAULT_STATE };
      this.loadFailed = false;
      this.layoutFrame = 0;

      if (!this.grid || !this.sectionId) return;

      this.readUrlState();
      this.bindControls();
      this.syncControls();
      this.root.classList.add('is-enhanced');
      this.loadAllPages();
    }

    readUrlState() {
      const params = new URLSearchParams(window.location.search);
      this.state.query = (params.get('topic_q') || '').trim();
      this.state.format = params.get('topic_format') || DEFAULT_STATE.format;
      this.state.level = params.get('topic_level') || DEFAULT_STATE.level;
      this.state.sort = params.get('topic_sort') || DEFAULT_STATE.sort;
    }

    bindControls() {
      this.root.querySelector(SELECTORS.search)?.addEventListener('input', (event) => {
        this.state.query = event.target.value.trim();
        this.apply();
      });

      this.root.querySelectorAll(SELECTORS.formatRadio).forEach((control) => {
        control.addEventListener('change', (event) => {
          if (!event.target.checked) return;
          this.state.format = event.target.value;
          this.syncControls();
          this.apply();
        });
      });

      this.root.querySelector(SELECTORS.formatSelect)?.addEventListener('change', (event) => {
        this.state.format = event.target.value;
        this.syncControls();
        this.apply();
      });

      this.root.querySelectorAll(SELECTORS.levelRadio).forEach((control) => {
        control.addEventListener('change', (event) => {
          if (!event.target.checked) return;
          this.state.level = event.target.value;
          this.syncControls();
          this.apply();
        });
      });

      this.root.querySelector(SELECTORS.levelSelect)?.addEventListener('change', (event) => {
        this.state.level = event.target.value;
        this.syncControls();
        this.apply();
      });

      this.root.querySelector(SELECTORS.sort)?.addEventListener('change', (event) => {
        this.state.sort = event.target.value;
        this.apply();
      });

      this.root.querySelectorAll(`${SELECTORS.clear}, ${SELECTORS.emptyReset}`).forEach((control) => {
        control.addEventListener('click', () => this.reset());
      });

      window.addEventListener('resize', () => this.scheduleCardSummarySync(), { passive: true });
    }

    syncControls() {
      const search = this.root.querySelector(SELECTORS.search);
      const formatSelect = this.root.querySelector(SELECTORS.formatSelect);
      const levelSelect = this.root.querySelector(SELECTORS.levelSelect);
      const sort = this.root.querySelector(SELECTORS.sort);

      if (search && search.value !== this.state.query) search.value = this.state.query;
      if (formatSelect && [...formatSelect.options].some((option) => option.value === this.state.format)) {
        formatSelect.value = this.state.format;
      } else {
        this.state.format = DEFAULT_STATE.format;
      }
      if (levelSelect && [...levelSelect.options].some((option) => option.value === this.state.level)) {
        levelSelect.value = this.state.level;
      } else if (levelSelect) {
        this.state.level = DEFAULT_STATE.level;
      } else if (!this.root.querySelector(SELECTORS.levelRadio)) {
        this.state.level = DEFAULT_STATE.level;
      }
      if (sort && [...sort.options].some((option) => option.value === this.state.sort)) {
        sort.value = this.state.sort;
      } else {
        this.state.sort = DEFAULT_STATE.sort;
        if (sort) sort.value = this.state.sort;
      }

      this.root.querySelectorAll(SELECTORS.formatRadio).forEach((control) => {
        control.checked = control.value === this.state.format;
      });
      this.root.querySelectorAll(SELECTORS.levelRadio).forEach((control) => {
        control.checked = control.value === this.state.level;
      });
    }

    async loadAllPages() {
      try {
        let next = this.root.querySelector(SELECTORS.next)?.href;
        const seen = new Set([...this.grid.querySelectorAll(SELECTORS.card)].map((card) => card.dataset.handle));

        while (next) {
          const url = new URL(next, window.location.origin);
          url.searchParams.set('section_id', this.sectionId);
          const response = await fetch(url.toString(), { credentials: 'same-origin' });
          if (!response.ok) throw new Error(`Resource page request failed with ${response.status}`);

          const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fetchedRoot = documentFragment.querySelector(SELECTORS.root);
          if (!fetchedRoot) throw new Error('Resource page section was not returned');

          fetchedRoot.querySelectorAll(SELECTORS.card).forEach((card) => {
            const handle = card.dataset.handle;
            if (!handle || seen.has(handle)) return;
            seen.add(handle);
            this.grid.append(card);
          });

          next = fetchedRoot.querySelector(SELECTORS.next)?.href || '';
        }
      } catch (error) {
        this.loadFailed = true;
        this.root.classList.remove('is-enhanced');
        console.warn('[SY Learning Platform topic library]', error);
      } finally {
        this.cards = [...this.grid.querySelectorAll(SELECTORS.card)];
        this.grid.setAttribute('aria-busy', 'false');
        this.root.classList.add('is-loaded');
        this.updateCounts();
        this.apply(false);
        this.scheduleCardSummarySync();
        if (document.fonts?.ready) {
          document.fonts.ready.then(() => this.scheduleCardSummarySync());
        }
      }
    }

    scheduleCardSummarySync() {
      window.cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = window.requestAnimationFrame(() => {
        this.layoutFrame = 0;
        this.syncCardSummaries();
      });
    }

    syncCardSummaries() {
      const isTopicPhone = window.matchMedia('(max-width: 749px)').matches;

      this.cards.forEach((card) => {
        const title = card.querySelector('.sy-home-one__resource-body h3');
        const description = card.querySelector('.sy-home-one__resource-description');
        if (!description) return;

        if (!isTopicPhone || !title) {
          description.style.removeProperty('--sy-mobile-description-lines');
          return;
        }

        const lineHeight = Number.parseFloat(window.getComputedStyle(title).lineHeight);
        const titleLines = lineHeight > 0
          ? Math.max(1, Math.round(title.getBoundingClientRect().height / lineHeight))
          : 1;
        const descriptionLines = Math.max(3, 6 - Math.min(titleLines, 3));
        description.style.setProperty('--sy-mobile-description-lines', String(descriptionLines));
      });
    }

    updateCounts() {
      const formats = new Set();
      const formatCounts = new Map();
      const levelCounts = new Map();

      this.cards.forEach((card) => {
        const format = card.dataset.format || '';
        const levels = (card.dataset.levels || '').split(/\s+/).filter(Boolean);
        if (format) {
          formats.add(format);
          formatCounts.set(format, (formatCounts.get(format) || 0) + 1);
        }
        levels.forEach((level) => levelCounts.set(level, (levelCounts.get(level) || 0) + 1));
      });

      this.root.querySelectorAll('[data-topic-format-count]').forEach((node) => {
        const format = node.dataset.topicFormatCount;
        node.textContent = format === 'all' ? this.cards.length : (formatCounts.get(format) || 0);
      });
      // Availability must use the full paginated library, not only the first 50 cards.
      this.root.querySelectorAll(SELECTORS.formatRadio).forEach((control) => {
        if (control.value !== 'all' && !formats.has(control.value)) control.closest('label')?.remove();
      });
      this.root.querySelector(SELECTORS.formatSelect)?.querySelectorAll('option').forEach((option) => {
        if (option.value !== 'all' && !formats.has(option.value)) option.remove();
      });
      if (this.state.format !== 'all' && !formats.has(this.state.format)) {
        this.state.format = DEFAULT_STATE.format;
        this.syncControls();
      }
      this.root.querySelectorAll('[data-topic-level-count]').forEach((node) => {
        const level = node.dataset.topicLevelCount;
        node.textContent = level === 'all' ? this.cards.length : (levelCounts.get(level) || 0);
      });

      const heroResourceCount = this.root.querySelector(SELECTORS.heroResourceCount);
      const heroFormatCount = this.root.querySelector(SELECTORS.heroFormatCount);
      const heroLevelFact = this.root.querySelector(SELECTORS.heroLevelFact);
      const heroLevelCount = this.root.querySelector(SELECTORS.heroLevelCount);
      const heroLevelNames = this.root.querySelector(SELECTORS.heroLevelNames);
      const representedLevels = [
        ['beginner', 'Beginner'],
        ['intermediate', 'Intermediate'],
        ['advanced', 'Advanced']
      ].filter(([level]) => (levelCounts.get(level) || 0) > 0);
      if (heroResourceCount) heroResourceCount.textContent = this.cards.length;
      if (heroFormatCount) heroFormatCount.textContent = formats.size;
      if (heroLevelCount) heroLevelCount.textContent = representedLevels.length;
      if (heroLevelNames) heroLevelNames.textContent = representedLevels.map(([, label]) => label).join(' \u00b7 ');
      if (heroLevelFact) heroLevelFact.hidden = representedLevels.length === 0;
    }

    apply(updateUrl = true) {
      if (!this.cards.length) this.cards = [...this.grid.querySelectorAll(SELECTORS.card)];

      const query = this.state.query.toLocaleLowerCase();
      const visible = [];

      this.cards.forEach((card) => {
        const matchesQuery = !query || (card.dataset.search || '').includes(query);
        const matchesFormat = this.state.format === 'all' || card.dataset.format === this.state.format;
        const levels = (card.dataset.levels || '').split(/\s+/).filter(Boolean);
        const matchesLevel = this.state.level === 'all' || levels.includes(this.state.level);
        const isVisible = matchesQuery && matchesFormat && matchesLevel;
        card.hidden = !isVisible;
        if (isVisible) visible.push(card);
      });

      const sortKey = this.state.sort === 'newest'
        ? 'newest'
        : this.state.sort === 'popular'
          ? 'popularity'
          : 'order';
      const direction = this.state.sort === 'recommended' ? 1 : -1;

      this.cards
        .slice()
        .sort((left, right) => {
          const difference = parseNumber(left.dataset[sortKey]) - parseNumber(right.dataset[sortKey]);
          return difference === 0
            ? parseNumber(left.dataset.order) - parseNumber(right.dataset.order)
            : difference * direction;
        })
        .forEach((card) => this.grid.append(card));

      const resultCount = this.root.querySelector(SELECTORS.count);
      const empty = this.root.querySelector(SELECTORS.empty);
      const clear = this.root.querySelector(SELECTORS.clear);
      const status = this.root.querySelector(SELECTORS.status);
      const isFiltered = Boolean(query) || this.state.format !== 'all' || this.state.level !== 'all' || this.state.sort !== 'recommended';

      if (resultCount) resultCount.textContent = visible.length;
      if (empty) empty.hidden = visible.length > 0;
      if (clear) clear.hidden = !isFiltered;
      if (status) status.textContent = `${visible.length} resources shown.`;
      if (updateUrl) this.updateUrl();
    }

    updateUrl() {
      const url = new URL(window.location.href);
      const values = {
        topic_q: this.state.query,
        topic_format: this.state.format,
        topic_level: this.state.level,
        topic_sort: this.state.sort
      };

      Object.entries(values).forEach(([key, value]) => {
        const defaultValue = DEFAULT_STATE[key.replace('topic_', '')] || '';
        if (!value || value === defaultValue) url.searchParams.delete(key);
        else url.searchParams.set(key, value);
      });
      window.history.replaceState({}, '', url);
    }

    reset() {
      this.state = { ...DEFAULT_STATE };
      this.syncControls();
      this.apply();
      this.root.querySelector(SELECTORS.search)?.focus();
    }
  }

  const init = () => document.querySelectorAll(SELECTORS.root).forEach((root) => new TopicLibrary(root));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
